import { FormEvent, useState } from 'react'

import Button from '../../../components/button/Button'
import NumberField from '../../../components/number-field/NumberField'
import ChipPicker from '../../../components/chip-picker/ChipPicker'
import ChoiceTileGroup from '../../../components/choice-tile/ChoiceTileGroup'
import { QuickPassDwellingScope } from '../../../_types/quickpass-profile'
import { OnboardingStepProps } from '../types'
import {
  StepBody,
  StepFooter,
  ErrorBanner,
  InlineFieldRow,
  Spacer,
} from '../styled'
import {
  BEDROOM_OPTIONS,
  BATHROOM_OPTIONS,
  DWELLING_SCOPE_OPTIONS,
} from '../helpers'

/**
 * Step 3: price range, bedroom + bathroom counts, dwelling scope.
 *
 * @param {OnboardingStepProps} props
 * @returns {JSX.Element}
 */
const PreferencesStep = ({
  profile,
  onSubmit,
  onBack,
  isSubmitting,
}: OnboardingStepProps): JSX.Element => {
  const initialPreferences = profile?.unitPreferences

  const [priceMin, setPriceMin] = useState<number | null>(
    initialPreferences?.priceMin ?? null,
  )
  const [priceMax, setPriceMax] = useState<number | null>(
    initialPreferences?.priceMax ?? null,
  )
  const [bedroomCounts, setBedroomCounts] = useState<number[]>(
    initialPreferences?.bedroomCounts ?? [],
  )
  const [bathroomCounts, setBathroomCounts] = useState<number[]>(
    initialPreferences?.bathroomCounts ?? [],
  )
  const [dwellingScope, setDwellingScope] = useState<QuickPassDwellingScope>(
    initialPreferences?.dwellingScope ?? 'no-preference',
  )
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    setErrorMessage('')

    if (priceMin !== null && priceMax !== null && priceMin > priceMax) {
      setErrorMessage('Min price has to be less than max price.')
      return
    }

    if (bedroomCounts.length === 0) {
      setErrorMessage('Pick at least one bedroom count — even Studio counts.')
      return
    }

    await onSubmit({
      unitPreferences: {
        priceMin,
        priceMax,
        bedroomCounts,
        bathroomCounts,
        dwellingScope,
        cities: initialPreferences?.cities ?? [],
        propertyTypes: initialPreferences?.propertyTypes ?? [],
        preferredMoveInDate: initialPreferences?.preferredMoveInDate ?? null,
      },
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <StepBody>
        <InlineFieldRow>
          <NumberField
            label="Min price / month"
            value={priceMin}
            onChange={setPriceMin}
            prefix="$"
            placeholder="1500"
            step={50}
            min={0}
          />
          <NumberField
            label="Max price / month"
            value={priceMax}
            onChange={setPriceMax}
            prefix="$"
            placeholder="2800"
            step={50}
            min={0}
          />
        </InlineFieldRow>
        <ChipPicker
          label="Bedrooms"
          options={BEDROOM_OPTIONS}
          selectedValues={bedroomCounts}
          onChange={setBedroomCounts}
          helperText="Pick all that work for you."
        />
        <ChipPicker
          label="Bathrooms (optional)"
          options={BATHROOM_OPTIONS}
          selectedValues={bathroomCounts}
          onChange={setBathroomCounts}
        />
        <ChoiceTileGroup
          label="Whole place or unit?"
          options={DWELLING_SCOPE_OPTIONS}
          selectedValue={dwellingScope}
          onChange={setDwellingScope}
        />
        {errorMessage !== '' ? <ErrorBanner>{errorMessage}</ErrorBanner> : null}
      </StepBody>
      <StepFooter>
        {onBack === null ? (
          <Spacer />
        ) : (
          <Button type="button" variant="ghost" onClick={onBack}>
            ← Back
          </Button>
        )}
        <Button type="submit" isLoading={isSubmitting}>
          Continue
        </Button>
      </StepFooter>
    </form>
  )
}

export default PreferencesStep
